import { useEffect } from "react";

import { needsResolvedArtworkOwnership } from "@/pages/artwork/utils/artworkView.selectors";
import fetchWrapper from "@/services/fetchWrapper.service";
import {
  createSuarteworldViewSource,
  resolveArtworkViewSource,
} from "@/shared/lib/artworkViewSource";

import { markArtworkForViewer, matchesArtworkRouteContext } from "./artworkFeedState.utils";
import {
  beginArtworkRequest,
  buildArtworkRequestKey,
  finishArtworkRequest,
  isCurrentArtworkRequest,
} from "./artworkRequestCoordinator";

export default function useArtworkLoadController({
  slug,
  artworks,
  activeSlide,
  viewerSessionId,
  holdingId,
  holdingOwnerId,
  holdingTypeParam,
  location,
  previousSlugRef,
  enrichedSlugsRef,
  markEnrichedSlug,
  logRouting,
  postVisitOnce,
  rehydrateArtworksFromCache,
  buildArtworkEndpoint,
  resolveHoldingContext,
  setLoading,
  setError,
  setArtworks,
  setActiveSlide,
  cacheHandler,
  artworkCacheKey,
  cacheInvalidationActions,
  pruneInaccessibleArtworkFromFeed,
  listCacheKeyRef,
  activeArtworkRequestRef,
}) {
  useEffect(() => {
    if (!slug) {
      return;
    }

    logRouting("load-effect-start", { reason: "slug-change-or-artworks-update" });
    postVisitOnce(slug);

    const hasArtworks = Array.isArray(artworks?.data);
    const cachedIndex = hasArtworks
      ? artworks.data.findIndex((artwork) => matchesArtworkRouteContext(artwork, {
        slug,
        holdingId,
        ownerId: holdingOwnerId,
        holdingType: holdingTypeParam,
      }))
      : -1;
    const cachedArtwork = cachedIndex !== -1 ? artworks.data[cachedIndex] : null;
    const slugChanged = previousSlugRef.current !== slug;
    const artworkEndpoint = buildArtworkEndpoint(slug);
    const requestKey = buildArtworkRequestKey(artworkEndpoint, viewerSessionId);

    if (hasArtworks && cachedIndex !== -1 && activeSlide !== cachedIndex && slugChanged) {
      logRouting("active-slide-sync", {
        fromActiveSlide: activeSlide,
        toActiveSlide: cachedIndex,
        slugMatched: slug,
      });
      setActiveSlide(cachedIndex);
    }

    const cachedAuction = cachedArtwork?.artwork_auction ?? null;
    const cachedAbout = cachedArtwork?.artwork_about ?? null;
    const shouldExpectAuctionData = cachedAbout?.artwork_is_auction === true;
    const hasAuctionBidHistory = Array.isArray(cachedAuction?.bids);
    const shouldEnrichAuction = Boolean(cachedArtwork) && shouldExpectAuctionData && !hasAuctionBidHistory;
    const slugAlreadyEnriched = slug ? enrichedSlugsRef.current.has(slug) : false;
    const cachedArtworkEnriched = cachedArtwork?._enriched === true;
    const needsOwnershipHydration = needsResolvedArtworkOwnership(cachedArtwork, viewerSessionId);
    // The route loader is the single owner of active-artwork hydration. A
    // cache entry can be enough to render a card, but not necessarily enough
    // for the full artwork view. Keeping this fetch here prevents the active
    // slide hydrator and recovery flow from replacing the same artwork with
    // competing responses during page entry.
    const shouldHydrateArtwork = Boolean(cachedArtwork)
      && ((!slugAlreadyEnriched && !cachedArtworkEnriched) || needsOwnershipHydration);
    const shouldFetchAuctionDetails = shouldEnrichAuction
      && !slugAlreadyEnriched
      && !cachedArtworkEnriched;
    const shouldFetchEnrichment = shouldHydrateArtwork || shouldFetchAuctionDetails;
    const shouldRefreshViewerState =
      Boolean(viewerSessionId)
      && Boolean(cachedArtwork)
      && cachedArtwork?._viewerStateHydratedFor !== viewerSessionId;

    if (!artworks || cachedIndex === -1) {
      logRouting("need-rehydrate-or-fetch", {
        hasArtworks,
        cachedIndex,
        reason: !artworks ? "no-artworks-payload" : "slug-not-found-in-current-payload",
      });
      const hydrated = rehydrateArtworksFromCache();
      if (hydrated) {
        logRouting("rehydrate-hit", { hydratedLength: hydrated?.data?.length ?? 0 });
        return;
      }

      setLoading(true);
      setError(null);

      const requestToken = beginArtworkRequest(activeArtworkRequestRef, requestKey);
      if (!requestToken) {
        return;
      }

      fetchWrapper.get(artworkEndpoint, {
        injectToken: true,
      })
        .then(({ response, data }) => {
          if (!isCurrentArtworkRequest(activeArtworkRequestRef, requestToken)) {
            return;
          }
          if (response.ok) {
            logRouting("single-artwork-fetch-success", {
              fetchedSlug: data?.slug ?? slug,
              status: response.status,
            });
            const sourceFromNavigation = resolveArtworkViewSource({
              navigationSource: location.state?.artworkViewSource,
              pathname: location.pathname,
            }) ?? (!location.state?.cacheKey ? createSuarteworldViewSource() : null);
            const enrichedData = {
              ...markArtworkForViewer(data, viewerSessionId),
              _enriched: true,
              ...(sourceFromNavigation ? { _artworkViewSource: sourceFromNavigation } : {}),
            };
            markEnrichedSlug(enrichedData.slug ?? slug);
            const holdingContext = resolveHoldingContext(enrichedData, holdingId, holdingOwnerId, holdingTypeParam);
            const dataToStore = {
              totalDocs: 1,
              data: [enrichedData],
              viewContext: holdingContext.holdingId ? { type: "holding", ...holdingContext } : undefined,
            };

            setArtworks(dataToStore);
            setActiveSlide(0);
            setLoading(false);
            cacheHandler.storeInCache(artworkCacheKey, dataToStore, {
              invalidateWhen: cacheInvalidationActions,
            });
          } else if (response.status === 404) {
            logRouting("single-artwork-fetch-not-found", { status: response.status });
            pruneInaccessibleArtworkFromFeed(
              {
                slug,
                holdingId,
                ownerId: holdingOwnerId,
                holdingType: holdingTypeParam,
              },
              { redirectIfActive: true, reason: "single-artwork-fetch-404" },
            );
            setLoading(false);
            setError(true);
          } else {
            logRouting("single-artwork-fetch-error-status", { status: response.status });
            setLoading(false);
            setError(true);
          }
        })
        .catch(() => {
          if (!isCurrentArtworkRequest(activeArtworkRequestRef, requestToken)) {
            return;
          }
          logRouting("single-artwork-fetch-exception");
          setLoading(false);
          setError(true);
        })
        .finally(() => {
          finishArtworkRequest(activeArtworkRequestRef, requestToken);
        });

      return;
    }

    if (shouldFetchEnrichment || shouldRefreshViewerState) {
      logRouting("artwork-refresh-needed", {
        slug,
        cachedIndex,
        needsArtworkHydration: shouldHydrateArtwork,
        needsAuctionEnrichment: shouldFetchAuctionDetails,
        needsViewerRefresh: shouldRefreshViewerState,
      });
      const shouldBlockWithLoader = shouldFetchAuctionDetails || needsOwnershipHydration;
      if (shouldBlockWithLoader) {
        setLoading(true);
      }
      setError(null);

      const requestToken = beginArtworkRequest(activeArtworkRequestRef, requestKey);
      if (!requestToken) {
        return;
      }

      fetchWrapper
        .get(artworkEndpoint, { injectToken: true })
        .then(({ response, data }) => {
          if (!isCurrentArtworkRequest(activeArtworkRequestRef, requestToken)) return;

          if (response.ok && data) {
            logRouting("artwork-refresh-success", {
              refreshedSlug: data?.slug ?? slug,
              refreshedViewerState: shouldRefreshViewerState,
              refreshedAuctionState: shouldFetchAuctionDetails,
            });
            const enrichedData = {
              ...markArtworkForViewer(data, viewerSessionId),
              _enriched: true,
            };
            markEnrichedSlug(enrichedData.slug ?? slug);
            const dataToStore = { totalDocs: 1, data: [enrichedData] };
            setArtworks((previousArtworks) => {
              if (previousArtworks && Array.isArray(previousArtworks.data)) {
                const nextData = [...previousArtworks.data];
                if (cachedIndex !== -1) {
                  nextData[cachedIndex] = {
                    ...nextData[cachedIndex],
                    ...enrichedData,
                    artwork_auction: enrichedData.artwork_auction ?? nextData[cachedIndex].artwork_auction ?? null,
                  };
                  return { ...previousArtworks, data: nextData };
                }
              }
              return dataToStore;
            });
            if (cachedIndex === -1) {
              listCacheKeyRef.current = null;
            }
            setActiveSlide(cachedIndex !== -1 ? cachedIndex : 0);
            if (shouldBlockWithLoader) {
              setLoading(false);
            }

            cacheHandler.storeInCache(`artwork_${slug}`, dataToStore, {
              invalidateWhen: cacheInvalidationActions,
            });
          } else if (response.status === 404) {
            logRouting("artwork-refresh-not-found", { status: response.status });
            pruneInaccessibleArtworkFromFeed(
              {
                slug,
                holdingId,
                ownerId: holdingOwnerId,
                holdingType: holdingTypeParam,
              },
              { redirectIfActive: true, reason: "artwork-refresh-404" },
            );
            if (shouldBlockWithLoader) {
              setLoading(false);
            }
            setError(true);
          } else {
            logRouting("artwork-refresh-error-status", { status: response.status });
            if (shouldBlockWithLoader) {
              setLoading(false);
            }
            setError(true);
          }
        })
        .catch(() => {
          if (!isCurrentArtworkRequest(activeArtworkRequestRef, requestToken)) return;
          logRouting("artwork-refresh-exception");
          if (shouldBlockWithLoader) {
            setLoading(false);
          }
          setError(true);
        })
        .finally(() => {
          finishArtworkRequest(activeArtworkRequestRef, requestToken);
        });

      return;
    }

    setLoading(false);
    setError(null);
  }, [
    activeSlide,
    artworkCacheKey,
    activeArtworkRequestRef,
    artworks,
    buildArtworkEndpoint,
    cacheHandler,
    cacheInvalidationActions,
    enrichedSlugsRef,
    holdingId,
    holdingOwnerId,
    holdingTypeParam,
    listCacheKeyRef,
    location,
    logRouting,
    markEnrichedSlug,
    postVisitOnce,
    previousSlugRef,
    pruneInaccessibleArtworkFromFeed,
    rehydrateArtworksFromCache,
    resolveHoldingContext,
    setActiveSlide,
    setArtworks,
    setError,
    setLoading,
    slug,
    viewerSessionId,
  ]);
}
